import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Typography,
  InputLabel,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import SaveIcon from "@mui/icons-material/Save";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import useStyles from "./BalSheetLiqRatioModelerGlobal.styles";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import {
  BALSHEETLIQRATIO,
  BSLR,
  MEASURE,
  DIMENSION,
} from "../../../utils/constants";
import {
  applyFilterForValues,
  getMappedBusinessEntityIDs,
  sliceObjectByIndex,
} from "../../../utils/utils";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext";
import ProgressSteps from "../../common/Steps/ProgressSteps";
import { useApprovalContext } from "../../../context/ApprovalContext";
import BalSheetLiqRatioModelerGlobalExport from "./BalSheetLiqRatioModelerGlobalExport";

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

const flattenReportData = (
  nestedData,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  if (!nestedData || !Array.isArray(nestedData)) {
    return [];
  }

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;

  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  return nestedData.map((row) => {
    const { datas, ...fixedProps } = row;
    const formattedDatas = {};

    if (datas) {
      for (const key in datas) {
        if (Object.hasOwnProperty.call(datas, key)) {
          let value = datas[key];

          if (shouldDivide) {
            value = value / divisor;
          }

          if (shouldFormatDecimals) {
            formattedDatas[key] = value.toFixed(decimals);
          } else {
            formattedDatas[key] = value;
          }
        }
      }
    }

    return {
      ...fixedProps,
      ...formattedDatas,
    };
  });
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const BalSheetLiqRatioModelerGlobalFiltersAndReports = ({
  toSave = false,
} = {}) => {
  const { classes } = useStyles();
  const [isWritableSfc, setIsWritableSfc] = useState(false);

  const { selectedUserDetailLoading, selectedUserDetail, isAdmin } =
    useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    hrGapResultDataLoading,
    hrGapResultDataError,
    hrGapResultData,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    localCurrency,
    setLocalCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    selectedMonthEnd,
    setSelectedMonthEnd,
    modelDatabases,
    setModelDatabases,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    fetchHrGapResultReport,
    setHrGapResultData,
  } = useHedgeRatioContext();

  const { metricsHistoryUpsertAndSave, metricsHistoryUpsertAndSaveLoading } =
    useTreasuryKRISummaryContext();

  const {
    fetchApprovalFlowData,
    approvalFlow,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const { regions, email } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency, writable } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
    setIsWritableSfc(writable);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  const APPROVAL_FLOW_PARAMS = {
    region: null,
    businessEntityID: selectedBusinessEntityID,
    selectedReportDate: selectedMonthEnd,
    isGlobal: true,
    isModelerReport: true,
    isTreasuryKRIReport: false,
    isKRISummaryReport: false,
    isSFCKRIGlobalSummaryReport: false,
    isRegionKRIGlobalSummaryReport: false,
    isTFSCKRIGlobalSummaryReport: false,
    measure: MEASURE.BALANCE_SHEET_LIQUIDITY_RATIO_KRI,
    dimension: DIMENSION.LIQUIDITY_RISK,
    dimension2: null,
  };

  const handleSubmitBtn = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      fetchHrGapResultReport(BSLR);
      fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
    } else {
      setHrGapResultData([]);
      setApprovalFlow([]);
      alert(
        "The selected Entity / Month End does not have any Model Database associated.",
      );
    }
  };

  const DATA_INDEX = 1;
  const DATA_LIMIT = 12;

  const dynamicColumns =
    hrGapResultData &&
    hrGapResultData?.length > 0 &&
    hrGapResultData?.[0]?.datas
      ? Object.keys(
          sliceObjectByIndex(
            hrGapResultData?.[0]?.datas,
            DATA_INDEX,
            DATA_LIMIT,
          ),
        )?.map((key) => {
          return {
            id: key,
            label: key,
            minWidth: 100,
            align: "right",
          };
        })
      : [];

  const columns = [
    {
      id: "reportingAccountName",
      label: "Balance Sheet Liquidity Ratio",
      minWidth: 160,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const exportColumns = [
    {
      id: "reportingAccountName",
      label: "Balance Sheet Liquidity Ratio",
      minWidth: 160,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const saveBalSheetMetricRatio = () => {
    metricsHistoryUpsertAndSave([
      {
        dimension: DIMENSION.LIQUIDITY_RISK,
        dimension2: null,
        measure: MEASURE.SHORT_TERM_ASSETS_12_MONTHS,
        businessEntityID: selectedBusinessEntityID,
        value: Number(formattedSumOf12MonthShortTermAssets)?.toLocaleString(),
        modelDatabase: selectedModelDatabase,
        isGlobal: true,
      },
      {
        dimension: DIMENSION.LIQUIDITY_RISK,
        dimension2: null,
        measure: MEASURE.SHORT_TERM_LIABILITIES_12_MONTHS,
        businessEntityID: selectedBusinessEntityID,
        value: Number(
          formattedSumOf12MonthShortTermLiabilities,
        )?.toLocaleString(),
        modelDatabase: selectedModelDatabase,
        isGlobal: true,
      },
      {
        dimension: DIMENSION.LIQUIDITY_RISK,
        dimension2: null,
        measure: MEASURE.BALANCE_SHEET_LIQUIDITY_RATIO_KRI,
        businessEntityID: selectedBusinessEntityID,
        value: `${balSheetLiqRatioPercent}%`,
        modelDatabase: selectedModelDatabase,
        isGlobal: true,
      },
    ]);
  };

  const preprocessedData = flattenReportData(
    hrGapResultData,
    selectedFigureDivisor,
    selectedDecimalsIn,
  );

  const preprocessedDataExport = flattenReportData(hrGapResultData, null, null);

  let sumOf12MonthShortTermAssets = 0;
  let sumOf12MonthShortTermLiabilities = 0;
  let formattedSumOf12MonthShortTermAssets = 0;
  let formattedSumOf12MonthShortTermLiabilities = 0;
  let balSheetLiqRatioPercent = 0;

  const { SHORT_TERM_ASSETS, SHORT_TERM_LIABILITIES } = BALSHEETLIQRATIO || {};

  hrGapResultData.forEach((data) => {
    if (data.reportingAccountName === SHORT_TERM_ASSETS) {
      sumOf12MonthShortTermAssets = Object.values(
        sliceObjectByIndex(data.datas, DATA_INDEX, DATA_LIMIT),
      ).reduce((sum, val) => sum + val, 0);
      formattedSumOf12MonthShortTermAssets = applyFilterForValues(
        sumOf12MonthShortTermAssets,
        selectedFigureDivisor,
        selectedDecimalsIn,
      );
    }
    if (data.reportingAccountName === SHORT_TERM_LIABILITIES) {
      sumOf12MonthShortTermLiabilities = Object.values(
        sliceObjectByIndex(data.datas, DATA_INDEX, DATA_LIMIT),
      ).reduce((sum, val) => sum + val, 0);
      formattedSumOf12MonthShortTermLiabilities = applyFilterForValues(
        sumOf12MonthShortTermLiabilities,
        selectedFigureDivisor,
        selectedDecimalsIn,
      );
    }
    if (
      sumOf12MonthShortTermAssets !== 0 &&
      sumOf12MonthShortTermLiabilities !== 0
    ) {
      balSheetLiqRatioPercent = (
        (sumOf12MonthShortTermAssets / sumOf12MonthShortTermLiabilities) *
        100
      )?.toFixed(2);
    }
  });

  const handleSendApproverEmailNotify = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => {
        return fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
      })
      .then(() => {
        return sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        });
      });
  };

  const handleSelectedModelDatabase = (modelDatabase) => {
    setSelectedModelDatabase(modelDatabase);
    const modelDatabaseObject = modelDatabases?.find(
      (modelDb) => modelDb.modelName === modelDatabase,
    );
    const rawDate = modelDatabaseObject?.monthEnd;
    if (rawDate) {
      setSelectedMonthEnd(dayjs(rawDate.replace("Z", "")));
    }
  };

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the Balance Sheet Liquidity Report (Modeler Global
        View) for the selected filters.
      </Typography>
      <>
        {!hrGapResultDataError &&
        hrGapResultData &&
        hrGapResultData?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pr: "10px",
              }}
            >
              {approvalFlow?.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSendApproverEmailNotify}
                  sx={{
                    fontSize: "0.8rem",
                    boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                    height: "38px",
                    mt: "-10px",
                  }}
                  disabled={
                    selectedUserDetailLoading ||
                    hrFiltersDataLoading ||
                    hrGapResultDataLoading ||
                    !selectedBusinessEntityID ||
                    !selectedModelDatabase ||
                    sendEmailNotifyLoading
                  }
                >
                  Send Reminder Notification
                </Button>
              )}
            </Box>

            <ProgressSteps
              stepsData={approvalFlow}
              isGlobal={true}
              isModelerReport={true}
              isTreasuryKRIReport={false}
              isKRISummaryReport={false}
              isSFCKRIGlobalSummaryReport={false}
              isRegionKRIGlobalSummaryReport={false}
              isTFSCKRIGlobalSummaryReport={false}
              reportDate={selectedMonthEnd}
              loggedInEmail={email}
              handleStepApproval={handleStepApproval}
            />
          </>
        ) : (
          <Typography
            sx={{
              color: "#6a6a6a",
              marginBottom: 2,
              fontStyle: "italic",
            }}
          >
            Please generate the report to view the Approval Progress
          </Typography>
        )}
      </>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"model-database"}>Model Database</InputLabel>
          <Autocomplete
            options={mappedModelDatabases}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedModelDatabases.find(
                (item) => item.id === selectedModelDatabase,
              ) || null
            }
            onChange={(_, newValue) => {
              handleSelectedModelDatabase(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Model Database"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitBtn}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading ||
              !selectedBusinessEntityID ||
              !selectedModelDatabase
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.balSheetLiqRatioExportBtn}>
            <BalSheetLiqRatioModelerGlobalExport
              columns={exportColumns}
              datas={preprocessedDataExport}
              disabled={
                hrGapResultDataLoading || !preprocessedDataExport?.length
              }
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!hrGapResultDataLoading && hrGapResultDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!hrGapResultDataLoading && !preprocessedData) ||
            (!hrGapResultDataLoading && !preprocessedData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <>
              <ColumnGroupingTable
                columns={columns}
                datas={preprocessedData}
                loading={hrGapResultDataLoading}
                tableHeight={150}
                freezeParentColumnCount={1}
                applyThousandsSeparator={true}
                disablePagination
              />
              {!hrGapResultDataLoading && (
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <Box sx={{ marginBottom: 2 }}>
                    <Table
                      sx={{
                        width: "30%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        borderRadius: "3px",
                        overflow: "hidden",
                        boxShadow: "1px 1px 2px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        backgroundColor: "#EBFFD6",
                      }}
                    >
                      <TableBody>
                        <TableRow
                          sx={{
                            "& td": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            <b>Short Term Assets (12 months)</b>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            {Number(
                              formattedSumOf12MonthShortTermAssets,
                            )?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& td": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            <b>Short Term Liabilities (12 months)</b>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            {Number(
                              formattedSumOf12MonthShortTermLiabilities,
                            )?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& td": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            <b>Balance Sheet Liquidity Ratio</b>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            {balSheetLiqRatioPercent}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    {false && toSave && (isWritableSfc || isAdmin) && (
                      <div className={classes.SaveBtn}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          startIcon={<SaveIcon />}
                          onClick={saveBalSheetMetricRatio}
                          sx={{
                            fontSize: "0.8rem",
                            boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                            height: "40px",
                          }}
                          disabled={metricsHistoryUpsertAndSaveLoading}
                        >
                          Save Data
                        </Button>
                      </div>
                    )}
                  </Box>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default BalSheetLiqRatioModelerGlobalFiltersAndReports;
